import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import useSeo from "../hooks/useSeo";
import PasswordInput from "../components/PasswordInput";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "",
        email: "",
        mobile_no: "",
        password: "",
        organization: "",
    });
    const [loading, setLoading] = useState(false);

    useSeo({ path: "/register" });

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6)
            return toast.error("Password must be at least 6 characters");
        if (!/^\d{10}$/.test(form.mobile_no))
            return toast.error("Mobile number must be exactly 10 digits");
        setLoading(true);
        try {
            await register(form);
            toast.success("Account created — pick a package to get started");
            nav("/pricing");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2"
            data-testid="register-page"
        >
            <div className="flex items-center justify-center p-6 md:p-12 order-2 md:order-1">
                <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
                    <div>
                        <h1 className="font-display font-black text-3xl tracking-tighter">
                            Create your account
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">
                            Free to sign up. Pay only for card packages.
                        </p>
                    </div>
                    <div>
                        <label className="label-swiss">Full Name</label>
                        <input
                            className="input-swiss"
                            value={form.name}
                            onChange={setField("name")}
                            required
                            data-testid="register-name-input"
                        />
                    </div>
                    <div>
                        <label className="label-swiss">
                            Organization / School
                        </label>
                        <input
                            className="input-swiss"
                            value={form.organization}
                            onChange={setField("organization")}
                            data-testid="register-org-input"
                        />
                    </div>
                    <div>
                        <label className="label-swiss">Email</label>
                        <input
                            type="email"
                            className="input-swiss"
                            value={form.email}
                            onChange={setField("email")}
                            required
                            data-testid="register-email-input"
                        />
                    </div>
                    <div>
                        <label className="label-swiss">Mobile Number</label>
                        <input
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]{10}"
                            maxLength={10}
                            className="input-swiss"
                            value={form.mobile_no}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    mobile_no: e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 10),
                                })
                            }
                            onKeyDown={(e) => {
                                const allowed = [
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                    "Home",
                                    "End",
                                ];
                                if (
                                    !allowed.includes(e.key) &&
                                    !/^\d$/.test(e.key) &&
                                    !(e.ctrlKey || e.metaKey)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                            placeholder="10-digit mobile"
                            required
                            data-testid="register-mobile-input"
                        />
                    </div>
                    <div>
                        <label className="label-swiss">Password</label>
                        <PasswordInput
                            value={form.password}
                            onChange={setField("password")}
                            autoComplete="new-password"
                            required
                            data-testid="register-password-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full"
                        data-testid="register-submit-btn"
                    >
                        {loading ? "Creating…" : "Create account"}
                    </button>
                    <p className="text-sm text-zinc-500">
                        Already registered?{" "}
                        <Link
                            to="/login"
                            className="text-blue-600 font-medium"
                            data-testid="register-goto-login"
                        >
                            Sign in
                        </Link>
                    </p>
                </form>
            </div>
            <div className="hidden md:flex flex-col justify-between p-12 bg-blue-600 text-white relative overflow-hidden order-1 md:order-2">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-200">
                        Get started
                    </div>
                    <h2 className="font-display font-black text-5xl tracking-tighter mt-3">
                        Print your first
                        <br />
                        card today.
                    </h2>
                </div>
                <div>
                    <ul className="space-y-2 text-sm text-blue-50">
                        <li>✓ Free account — no credit card</li>
                        <li>✓ 3 flexible packages to fit any institution</li>
                        <li>✓ Admin verifies your payment in hours</li>
                        <li>✓ Unlimited template designs</li>
                    </ul>
                </div>
                <div className="text-xs font-mono text-blue-200 uppercase tracking-widest">
                    IDCardGen · Fast · Reliable
                </div>
            </div>
        </div>
    );
}
